import * as signalR from '@microsoft/signalr';
import configAPI from '../api/config';

// One SignalR connection for the whole app. Components call acquireChatConnection()
// and release it on unmount; the connection starts with the first user and stops with
// the last, so re-renders and multiple consumers never open a second connection.

export const CHAT_CONNECTION_STATE = {
  CONNECTING: 'Connecting',
  CONNECTED: 'Connected',
  RECONNECTING: 'Reconnecting',
  DISCONNECTED: 'Disconnected',
};

export const CHAT_EVENTS = {
  RECEIVE_MESSAGE: 'ReceiveMessage',
  MESSAGES_READ: 'MessagesRead',
  USER_TYPING: 'UserTyping',
  PRESENCE_CHANGED: 'PresenceChanged',
  // The user was added to or removed from a group, or one of their groups was renamed or deleted.
  GROUP_CHANGED: 'GroupChanged',
};

const RETRY_BASE_DELAY_MS = 5000;
const RETRY_MAX_DELAY_MS = 60000;

const getHubUrl = () => {
  if (configAPI.REACT_APP_CHAT_HUB_URL) return configAPI.REACT_APP_CHAT_HUB_URL;
  const base = (configAPI.REACT_APP_CHAT_API_URL || '').replace(/\/+$/, '');
  return `${base}/hubs/chat`;
};

let connection = null;
let userCount = 0;
let retryTimer = null;
let retryAttempt = 0;
let state = CHAT_CONNECTION_STATE.DISCONNECTED;

const stateListeners = new Set();
const eventHandlers = Object.values(CHAT_EVENTS).reduce((acc, name) => {
  acc[name] = new Set();
  return acc;
}, {});

const setState = (next) => {
  if (state === next) return;
  state = next;
  stateListeners.forEach(listener => listener(next));
};

const scheduleRetry = () => {
  if (userCount === 0 || retryTimer) return;
  // Back off so an unreachable chat server is not hit every few seconds forever.
  const delay = Math.min(RETRY_BASE_DELAY_MS * 2 ** retryAttempt, RETRY_MAX_DELAY_MS);
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    start();
  }, delay);
};

const buildConnection = () => {
  const built = new signalR.HubConnectionBuilder()
    .withUrl(getHubUrl(), {
      // Read on every (re)connect so a token refreshed by baseFetch is picked up.
      accessTokenFactory: () => localStorage.getItem('token') || '',
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  Object.keys(eventHandlers).forEach(name => {
    built.on(name, payload => eventHandlers[name].forEach(handler => handler(payload)));
  });

  built.onreconnecting(() => {
    if (connection === built) setState(CHAT_CONNECTION_STATE.RECONNECTING);
  });
  built.onreconnected(() => {
    if (connection === built) setState(CHAT_CONNECTION_STATE.CONNECTED);
  });
  // Fires when automatic reconnect gives up (or the server aborts the connection).
  built.onclose(() => {
    if (connection !== built) return;
    setState(CHAT_CONNECTION_STATE.DISCONNECTED);
    scheduleRetry();
  });

  return built;
};

const start = async () => {
  if (userCount === 0) return;
  if (!connection) connection = buildConnection();
  if (connection.state !== signalR.HubConnectionState.Disconnected) return;

  const current = connection;
  setState(CHAT_CONNECTION_STATE.CONNECTING);
  try {
    await current.start();
    if (connection === current) {
      retryAttempt = 0;
      setState(CHAT_CONNECTION_STATE.CONNECTED);
    }
  } catch (err) {
    if (connection === current) {
      setState(CHAT_CONNECTION_STATE.DISCONNECTED);
      scheduleRetry();
    }
  }
};

const stop = () => {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  retryAttempt = 0;
  const current = connection;
  connection = null;
  setState(CHAT_CONNECTION_STATE.DISCONNECTED);
  if (current) current.stop().catch(() => {});
};

/** Registers a user of the connection. Returns the release function. */
export const acquireChatConnection = () => {
  userCount += 1;
  start();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    userCount -= 1;
    if (userCount === 0) stop();
  };
};

export const getChatConnectionState = () => state;

export const isChatConnected = () =>
  !!connection && connection.state === signalR.HubConnectionState.Connected;

/** Subscribes to connection state changes. Returns the unsubscribe function. */
export const onChatConnectionStateChange = (listener) => {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
};

/** Subscribes to a server event (see CHAT_EVENTS). Returns the unsubscribe function. */
export const onChatEvent = (eventName, handler) => {
  eventHandlers[eventName].add(handler);
  return () => eventHandlers[eventName].delete(handler);
};

const invoke = (method, ...args) => {
  if (!isChatConnected()) {
    return Promise.reject(new Error('Chat is not connected.'));
  }
  return connection.invoke(method, ...args);
};

export const sendChatMessageRealtime = (conversationId, body, clientMessageId) =>
  invoke('SendMessage', conversationId, body, clientMessageId);

export const markChatReadRealtime = (conversationId) =>
  invoke('MarkRead', conversationId);

export const sendChatTyping = (conversationId) =>
  invoke('Typing', conversationId).catch(() => {});

/**
 * The server rejects a call with a HubException whose text is safe to show.
 * Returns that text, or null when the failure was a transport/connection problem.
 */
export const getHubRejectionMessage = (err) => {
  const text = err?.message || '';
  const marker = 'HubException:';
  const index = text.indexOf(marker);
  return index === -1 ? null : text.slice(index + marker.length).trim();
};
